import React, { useCallback, useMemo, useState } from "react";
import { View } from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import Animated, { withSpring, withDelay, useAnimatedStyle } from "react-native-reanimated";
import { useI18nMessage } from "@lookiero/i18n-react";
import { Text } from "@lookiero/sty-psp-ui";
import { DOMAIN, I18nMessages, PREFIX } from "../../../i18n/i18n";
import { ItemStatus } from "../../itemStatus";
import { ActionButtonStatus } from "../../molecules/actionButton/ActionButton";
import { Card } from "../../molecules/card/Card";
import { Dislike } from "../../molecules/dislike/Dislike";
import { Like } from "../../molecules/like/Like";
import { HowToModal } from "../../organisms/howToModal/HowToModal";
import { Title } from "../../organisms/title/Title";
import { style as lookAndLikeStyle } from "./LookAndLike.style";
const LookAndLike = ({ gameProductVariants, lastGame, headerless = false, style: customStyle, portalHostName, onChange, }) => {
    const descriptionText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.DESCRIPTION, prefix: PREFIX });
    const lastGameTitleText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.LAST_GAME_TITLE, prefix: PREFIX });
    const lastGameDescriptionTEXT = useI18nMessage({
        domain: DOMAIN,
        id: I18nMessages.LAST_GAME_DESCRIPTION,
        prefix: PREFIX,
    });
    const successTitleText = useI18nMessage({ domain: DOMAIN, id: I18nMessages.SUCCESS_TITLE, prefix: PREFIX });
    const successDescriptionText = useI18nMessage({
        domain: DOMAIN,
        id: I18nMessages.SUCCESS_DESCRIPTION,
        prefix: PREFIX,
    });
    const style = useMemo(() => lookAndLikeStyle(), []);
    const [active, setActive] = useState(0);
    const [values, setValues] = useState([]);
    const [howToModalVisible, setHowToModalVisiable] = useState(false);
    const handleOnDismissHowToModal = useCallback(() => setHowToModalVisiable(false), []);
    const handleOnPressTitle = useCallback(() => setHowToModalVisiable(true), []);
    const [itemStatus, setItemStatus] = useState(undefined);
    const handleItemStatusChanged = useCallback((status) => {
        if (!status) {
            return;
        }
        const liked = status === ItemStatus.LIKED;
        setActive(active + 1);
        setValues([...values, liked]);
        const item = gameProductVariants[active];
        if (item) {
            onChange({ id: item.id, like: liked });
        }
    }, [active, gameProductVariants, onChange, values]);
    const handleLikePressed = useCallback(() => handleItemStatusChanged(ItemStatus.LIKED), [handleItemStatusChanged]);
    const handleDislikePressed = useCallback(() => handleItemStatusChanged(ItemStatus.DISLIKED), [handleItemStatusChanged]);
    const finished = active === gameProductVariants.length;
    const textAnimationStyle = useAnimatedStyle(() => ({
        opacity: withSpring(finished ? 1 : 0),
        transform: [{ scale: withSpring(finished ? 1 : 0.8) }],
    }), [finished]);
    const buttonsAnimationStyle = useAnimatedStyle(() => ({
        opacity: withSpring(finished ? 0 : 1),
        transform: [{ scale: withSpring(finished ? 0.8 : 1) }],
    }), [finished]);
    const delayedTextAnimationStyle = useAnimatedStyle(() => ({
        opacity: withDelay(300, withSpring(finished ? 1 : 0)),
        transform: [{ scale: withDelay(300, withSpring(finished ? 1 : 0.8)) }],
    }), [finished]);
    return (React.createElement(React.Fragment, null,
        React.createElement(HowToModal, { portalHostName: portalHostName, visible: howToModalVisible, onDismiss: handleOnDismissHowToModal }),
        React.createElement(View, { style: [style.container, customStyle], testID: "look-and-like" },
            !headerless && React.createElement(Title, { onPress: handleOnPressTitle }),
            React.createElement(View, { style: style.content },
                React.createElement(GestureHandlerRootView, { style: style.cards }, gameProductVariants.map(({ id, image }, index) => (React.createElement(Card, { key: id, activeIndex: active, imageUrl: image, index: index, status: values[index] !== undefined ? (values[index] ? ItemStatus.LIKED : ItemStatus.DISLIKED) : undefined, testID: `look-and-like-card-${id}`, onStatusChanged: handleItemStatusChanged, onStatusChanging: setItemStatus })))),
                React.createElement(Animated.View, { pointerEvents: "none", style: [style.success, textAnimationStyle] },
                    React.createElement(Text, { level: 2, selectable: false, style: style.successText, variant: "heading" }, lastGame ? lastGameTitleText : successTitleText))),
            React.createElement(Text, { level: 1, selectable: false, style: [style.descriptionText, { opacity: finished ? 0 : 1 }] }, descriptionText),
            React.createElement(View, { style: style.buttonsContainer },
                gameProductVariants.length > 0 && (React.createElement(Animated.View, { pointerEvents: finished ? "none" : undefined, style: buttonsAnimationStyle },
                    React.createElement(View, { style: style.likeDislikeContainer },
                        React.createElement(Dislike, { status: itemStatus === ItemStatus.DISLIKED
                                ? ActionButtonStatus.ACTIVE
                                : itemStatus === ItemStatus.LIKED
                                    ? ActionButtonStatus.INACTIVE
                                    : ActionButtonStatus.IDLE, onPress: handleDislikePressed }),
                        React.createElement(Like, { status: itemStatus === ItemStatus.LIKED
                                ? ActionButtonStatus.ACTIVE
                                : itemStatus === ItemStatus.DISLIKED
                                    ? ActionButtonStatus.INACTIVE
                                    : ActionButtonStatus.IDLE, onPress: handleLikePressed })))),
                React.createElement(Animated.View, { pointerEvents: "none", style: [style.finished, delayedTextAnimationStyle] },
                    React.createElement(Text, { level: 3, selectable: false, style: style.finishedText, variant: "detail" }, lastGame ? lastGameDescriptionTEXT : successDescriptionText))))));
};
export { LookAndLike };
